import { useAuth } from '@/hooks/use-auth';
import { getUserPreferences } from '@/lib/api';
import { USER_PREFERENCES_QUERY_KEY } from '@/lib/report/pdfImageColumns';
import { useQuery } from '@tanstack/react-query';
const DEFAULTS = {
    // Scalars are null until the server answers: formatting reads the general
    // settings storage, never these, so nothing renders en-US by accident.
    timezone: null,
    timeFormat: null,
    locale: null,
    sidebarClinicDisplayName: '',
    reportSettings: {
        // null = follow the clinic's report design (matches the server default).
        pdfImageColumns: null,
        signatureBlock: '',
        autoSave: true,
        dictationKeyterms: [],
        dictationTriggerMode: 'toggle',
        dictationVadSilenceMs: 8000,
        dictationEngine: 'newlumen',
        dictationLanguage: null,
        dictationLocale: null,
        dictationProvider: null,
        dictationModel: null,
        dictationVoiceBindings: [],
        // null = never chosen: echo reference ranges show (matches the server default).
        showEchoReferenceRanges: null,
    },
    multiScreen: { enabled: false, openFullscreen: false },
};
// The key is defined beside the PDF helpers that read the cache directly (see
// lib/report/pdfImageColumns.ts); re-exported here so existing imports hold.
export { USER_PREFERENCES_QUERY_KEY };
/**
 * Reads /users/me/preferences via React Query so all consumers share a single
 * cached fetch. Writers (e.g. settings UI) should invalidate
 * USER_PREFERENCES_QUERY_KEY after a successful PUT to fan out updates.
 *
 * Returns DEFAULTS while the query is loading, after a failed read, and for
 * unauthenticated mounts, so consumers can rely on a stable shape for
 * DISPLAY without gating on isLoading.
 *
 * Writers must gate on `isLoaded` instead: it is true only once the stored
 * preferences have actually been read. `isLoading` goes false after a failed
 * GET too, leaving DEFAULTS standing in, and a PUT built from them replaces
 * every stored setting in the record it writes.
 */
export function useUserPreferences() {
    const { isAuthenticated } = useAuth();
    const { data, isLoading } = useQuery({
        queryKey: USER_PREFERENCES_QUERY_KEY,
        queryFn: getUserPreferences,
        staleTime: 5 * 60 * 1000,
        enabled: isAuthenticated,
    });
    return { preferences: data ?? DEFAULTS, isLoading, isLoaded: data !== undefined };
}
